/* ==========================================================================
   What people say — hide the band while the reviews app has nothing to show.
   --------------------------------------------------------------------------
   A review app renders client-side. When the shop has no visible reviews it
   draws nothing and collapses to zero height, which leaves our heading sitting
   over an empty band — the footer-column failure again: it reads as broken
   rather than as unfinished.

   This watches the app block's slot and mirrors its emptiness onto the whole
   section. It is deliberately vendor-neutral: it measures height and reads no
   class names, no globals and no app API. The slot is an `@app` block and is
   not promised to be any particular app, and a selector like `.jdgm-hidden`
   would break silently the first time the app renamed it.

   Visible by default, hidden only after it settles
   -----------------------------------------------
   The band ships visible and this hides it, rather than shipping hidden and
   revealing it. Two reasons. A browser that never runs this file also never
   gets the app's reviews, so it loses nothing it could have had. And on a slow
   connection the reviews arrive late — starting hidden would mean the band
   popped in every single time it worked, whereas starting visible only shifts
   when there is genuinely nothing there.

   The settle delay exists so a slow widget is not mistaken for an empty one.
   The band sits far down the page, so a collapse inside that window is over
   long before anyone scrolls to it, and it is not a layout shift anyone sees.

   Either way it is self-healing: if the slot ever fills, the band comes back,
   so nobody has to remember to switch a section on when the first real review
   lands.
   ========================================================================== */

/* Long enough that a slow or retrying widget is not read as an empty one —
   Judge.me gives up and logs its own timeout well inside this. */
const SETTLE_MS = 4000;

class CookedReviews extends HTMLElement {
  connectedCallback() {
    /* Never in the theme editor. A section that hides itself cannot be
       selected, and the editor is the one place the emptiness is fixable. */
    if (this.dataset.editor === 'true') return;

    /* Not `this.slot`. `slot` is a real property on Element — it reflects the
       slot attribute and coerces whatever you assign to a string — so storing
       the node there silently turns it into "[object HTMLDivElement]" and the
       observer then rejects it. Found by rendering; it throws nowhere else. */
    this.blocksEl = this.querySelector('[data-reviews-blocks]');
    if (!this.blocksEl) return;

    this.settled = false;
    this.sync = this.sync.bind(this);

    /* ResizeObserver fires once on observe with the current size, so this
       needs no separate initial measurement. */
    this.observer = new ResizeObserver(this.sync);
    this.observer.observe(this.blocksEl);

    this.timer = setTimeout(() => {
      this.settled = true;
      this.sync();
    }, SETTLE_MS);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this.timer);
  }

  sync() {
    /* Measured on the slot, never on the host. The collapsed host still has a
       box (see the CSS — that is the whole reason it is not `display: none`),
       and the slot's own box keeps its true height inside it, so this reads
       correctly whether the band is showing or collapsed. */
    const filled = this.blocksEl.getBoundingClientRect().height > 0;

    if (filled) {
      /* Content arrived. Show it, and stop waiting — from here the observer
         alone is enough to react if it ever empties again. */
      this.removeAttribute('data-empty');
      clearTimeout(this.timer);
      this.settled = true;
      return;
    }

    /* Empty, but possibly only for now. Collapse once, and only once we have
       waited long enough to be sure. */
    if (this.settled) this.setAttribute('data-empty', '');
  }
}

if (!customElements.get('cooked-reviews')) {
  customElements.define('cooked-reviews', CookedReviews);
}
